import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, HelpCircle, Phone, Mail, MessageCircle, ChevronDown, ChevronUp, Coins, RefreshCw, Gift, Shield, CreditCard, Clock, Info } from 'lucide-react';

const HelpPageNew = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('all');
  const [expandedFaq, setExpandedFaq] = useState(null);

  const categories = [
    { id: 'all', label: 'All', icon: HelpCircle },
    { id: 'getting_started', label: 'Getting Started', icon: Coins },
    { id: 'booking', label: 'Book Millis', icon: CreditCard },
    { id: 'refunds', label: 'Refund Millis', icon: RefreshCw },
    { id: 'redeeming', label: 'Redeem Gold', icon: Gift },
    { id: 'security', label: 'Account & Security', icon: Shield },
    { id: 'general', label: 'General', icon: Info }
  ];

  const faqs = [
    {
      id: 1,
      category: 'getting_started',
      question: "What is Mint a Milli?",
      answer: "Mint a Milli is a digital gold booking platform by Mothers Gold & Diamonds LLP. It allows you to book 22KT gold in small units called 'Millis' (1 Milli = 0.001 grams of gold). You can book, refund, or redeem your gold at any time."
    },
    {
      id: 2,
      category: 'getting_started',
      question: "How do I create an account?",
      answer: "Simply enter your mobile number on the login screen. You'll receive an OTP (One-Time Password) via SMS. Enter the OTP to verify your number and your account will be created automatically."
    },
    {
      id: 3,
      category: 'getting_started',
      question: "What is KYC and why is it required?",
      answer: "KYC (Know Your Customer) is a verification process required by Indian regulations. Aadhaar verification is required for booking, refunding and redeeming gold. You can verify by scanning (mobile app) or uploading (website) the QR code on your Aadhaar card or e-Aadhaar, or by entering your Aadhaar details manually. PAN verification becomes mandatory once your total bookings reach ₹2,00,000."
    },
    {
      id: 4,
      category: 'getting_started',
      question: "Can I use the same Aadhaar on more than one account?",
      answer: "No. Each Aadhaar can be linked to only one active Mint a Milli account. If your Aadhaar is already linked to another account, verification will be blocked and the masked phone number of that account will be shown. Please contact our support team if you need to move your gold to a new account."
    },
    {
      id: 5,
      category: 'booking',
      question: "What is the minimum amount I can book?",
      answer: "The minimum booking amount is ₹100. You can book any amount above this. Quick-select amounts are shown on the booking screen for convenience."
    },
    {
      id: 6,
      category: 'booking',
      question: "How is the gold rate determined?",
      answer: "We fetch the live 22KT gold rate per gram from the All Kerala Gold & Silver Merchants Association (akgsma.com). The rate is refreshed every 30 minutes, and the 22KT rate shown next to the Booking Rate is the exact rate published by AKGSMA."
    },
    {
      id: 7,
      category: 'booking',
      question: "What is the Booking Rate?",
      answer: "The Booking Rate is the price of 1 Milli when you book gold. It is calculated as: 22KT Gold Rate per gram ÷ 1000, rounded UP to the nearest 10 paise. For example, if the 22KT rate is ₹12,795/gram: 12,795 ÷ 1000 = ₹12.795, which is rounded up to a Booking Rate of ₹12.80/Milli. If the rate divides evenly (e.g. ₹12,800/gram), no rounding is needed and the Booking Rate is ₹12.80/Milli."
    },
    {
      id: 8,
      category: 'booking',
      question: "What payment methods are accepted?",
      answer: "We accept UPI payments through HDFC Bank's secure payment integration. You can pay using any UPI app (Google Pay, PhonePe, Paytm, BHIM, etc.). All transactions are protected with bank-grade encryption."
    },
    {
      id: 9,
      category: 'refunds',
      question: "How do I get a refund for my booked Millis?",
      answer: "Go to the Refund Millis section, enter the number of Millis you want to refund (minimum 50 Millis), and confirm. The refund will be processed to your registered bank account within 24-48 hours."
    },
    {
      id: 10,
      category: 'refunds',
      question: "What is the refund rate?",
      answer: "The Refund Rate is the amount you receive for each Milli you refund. It is calculated as: 98% of the 22KT Gold Rate per gram ÷ 1000, rounded DOWN to the nearest 10 paise. For example, if the 22KT rate is ₹12,795/gram: 98% of 12,795 = ₹12,539.10, ÷ 1000 = ₹12.539, which is rounded down to a Refund Rate of ₹12.50/Milli. The 2% margin covers operational costs."
    },
    {
      id: 11,
      category: 'refunds',
      question: "Why do I need bank details for refund?",
      answer: "Bank details are required to process your refund amount. Please ensure your account number and IFSC code are correct to avoid delays. We support all major Indian banks."
    },
    {
      id: 12,
      category: 'refunds',
      question: "Can I cancel a refund request?",
      answer: "Once a refund is initiated, it cannot be cancelled as the Millis are immediately deducted from your balance. Please review your refund amount carefully before confirming."
    },
    {
      id: 13,
      category: 'redeeming',
      question: "What is gold redemption?",
      answer: "Redemption allows you to convert your Millis into physical 22KT gold jewellery. You'll receive a Gold Voucher that can be exchanged for jewellery at any Mothers Gold & Diamonds showroom."
    },
    {
      id: 14,
      category: 'redeeming',
      question: "What's the minimum amount for redemption?",
      answer: "You need at least 1000 Millis (equivalent to 1 gram of 22KT gold) to redeem. This ensures the gold can be physically weighed and verified at our showrooms."
    },
    {
      id: 15,
      category: 'redeeming',
      question: "How long is the gold voucher valid?",
      answer: "Gold vouchers are valid for 30 days from the date of issue. Please visit a Mothers Gold & Diamonds showroom within this period to collect your gold jewellery."
    },
    {
      id: 16,
      category: 'redeeming',
      question: "Are there any making charges?",
      answer: "No making charges are deducted from your gold weight when you redeem. You receive the full gram value of your Millis as jewellery."
    },
    {
      id: 17,
      category: 'redeeming',
      question: "Can I get gold delivered to my address?",
      answer: "Currently, gold can only be collected from our showrooms for security reasons. We're working on secure home delivery options for the future."
    },
    {
      id: 18,
      category: 'security',
      question: "Is my data safe with Mint a Milli?",
      answer: "Yes, we use industry-standard encryption and secure servers to protect your data. Your personal information is never shared with third parties. All payments are processed through HDFC Bank's secure payment gateway."
    },
    {
      id: 19,
      category: 'security',
      question: "What if I don't receive the OTP?",
      answer: "Wait for 60 seconds and tap 'Resend OTP'. Ensure your phone has network connectivity. If the issue persists, contact our support team for assistance."
    },
    {
      id: 20,
      category: 'security',
      question: "How do I update my address?",
      answer: "Open your profile and edit Personal Information to add or update your address. Once your KYC is verified, your address is locked to the verified details. Please contact our support team if it needs to be changed."
    },
    {
      id: 21,
      category: 'security',
      question: "Can I change my registered phone number?",
      answer: "Currently, the registered phone number cannot be changed directly. Please contact our support team with your KYC documents for assistance with account-related changes."
    },
    {
      id: 22,
      category: 'security',
      question: "What happens if a payment fails?",
      answer: "If a payment fails or is not completed, the transaction is automatically marked as 'Failed' within 30 minutes. If money was debited but Millis weren't credited, contact support with your transaction ID."
    },
    {
      id: 23,
      category: 'general',
      question: "How do referral rewards work?",
      answer: "Share your referral code from your profile (Refer & Earn on mobile, Your Referral Code on the website). When a friend signs up with your code and completes their first booking, you receive a gold reward, credited as Millis at that day's Booking Rate. The reward amount may change from time to time."
    },
    {
      id: 24,
      category: 'general',
      question: "Is Mint a Milli an investment platform?",
      answer: "No, Mint a Milli is a gold booking platform, not an investment scheme. You're booking physical gold at current market rates. The gold value may fluctuate with market prices."
    },
    {
      id: 25,
      category: 'general',
      question: "Who operates Mint a Milli?",
      answer: "Mint a Milli is owned and operated by Mothers Gold & Diamonds LLP, a registered gold jewellery manufacturer and wholesaler based in Kerala, India."
    },
    {
      id: 26,
      category: 'general',
      question: "What are the support hours?",
      answer: "Our support team is available Monday to Saturday, 10 AM to 6 PM. You can reach us via phone at +91 8137 830 916 or email at support@mothersgold.in."
    },
    {
      id: 27,
      category: 'general',
      question: "Can I add a nominee to my account?",
      answer: "Yes, you can add a nominee in the KYC section. Nominee details include name, relationship, and optionally their Aadhaar number. This ensures your gold holdings can be transferred in case of unforeseen circumstances."
    }
  ];

  const filteredFaqs = activeCategory === 'all' 
    ? faqs 
    : faqs.filter(faq => faq.category === activeCategory);

  const toggleFaq = (id) => {
    setExpandedFaq(expandedFaq === id ? null : id);
  };

  return (
    <div className="min-h-screen pb-8" data-testid="help-page-new">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#004D40] to-[#00302B] px-4 py-4 sticky top-16 z-10 mx-2 rounded-lg shadow-lg">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <div>
            <h1 className="text-xl text-white font-semibold" style={{ fontFamily: 'Georgia, serif' }}>
              Help & Support
            </h1>
            <p className="text-xs text-white/70">We&apos;re here to help you</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Contact Card */}
        <div className="bg-white/90 backdrop-blur-sm rounded-xl border border-[#B8860B]/30 p-6 mb-6 shadow-lg">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#004D40] to-[#00302B] flex items-center justify-center">
              <MessageCircle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[#004D40]" style={{ fontFamily: 'Georgia, serif' }}>
                Need Assistance?
              </h2>
              <p className="text-sm text-[#B8860B]">Our support team is ready to help</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
            <Clock className="w-4 h-4 text-[#004D40]" />
            <span>Mon - Sat, 10:00 AM - 6:00 PM</span>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <a 
              href="tel:+918137830916" 
              className="flex items-center justify-center gap-2 py-3 px-4 bg-gradient-to-r from-[#004D40] to-[#00302B] text-white font-semibold rounded-xl hover:opacity-90 transition-opacity"
              data-testid="call-button"
            >
              <Phone className="w-4 h-4" />
              Call Us
            </a>
            <a 
              href="mailto:support@mothersgold.in" 
              className="flex items-center justify-center gap-2 py-3 px-4 bg-gradient-to-r from-[#B8860B] to-[#DAA520] text-white font-semibold rounded-xl hover:opacity-90 transition-opacity"
              data-testid="email-button"
            >
              <Mail className="w-4 h-4" />
              Email Us
            </a>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="mb-6 overflow-x-auto">
          <div className="flex gap-2 pb-2">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  activeCategory === cat.id
                    ? 'bg-[#004D40] text-white shadow-md'
                    : 'bg-white/80 text-gray-600 border border-gray-200 hover:border-[#004D40]/50'
                }`}
                data-testid={`category-${cat.id}`}
              >
                <cat.icon className="w-4 h-4" />
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* FAQ Section */}
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-[#004D40] mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            Frequently Asked Questions
          </h3>
          
          {filteredFaqs.map((faq) => (
            <div 
              key={faq.id}
              className="bg-white/90 backdrop-blur-sm rounded-xl border border-[#B8860B]/20 overflow-hidden shadow-sm"
            >
              <button
                onClick={() => toggleFaq(faq.id)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-[#F5E6C8]/30 transition-colors"
                data-testid={`faq-${faq.id}`}
              >
                <span className="text-sm font-medium text-gray-800 pr-4">{faq.question}</span>
                {expandedFaq === faq.id ? (
                  <ChevronUp className="w-5 h-5 text-[#004D40] flex-shrink-0" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-[#B8860B] flex-shrink-0" />
                )}
              </button>
              {expandedFaq === faq.id && (
                <div className="px-4 pb-4 text-sm text-gray-600 leading-relaxed border-t border-[#B8860B]/10 pt-3 bg-[#F5E6C8]/20">
                  {faq.answer}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Additional Help */}
        <div className="mt-8 bg-gradient-to-r from-[#F5E6C8] to-[#EDD9B5] rounded-xl p-6 border border-[#B8860B]/30">
          <h3 className="text-base font-semibold text-[#004D40] mb-2" style={{ fontFamily: 'Georgia, serif' }}>
            Still have questions?
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Can&apos;t find what you&apos;re looking for? Our support team is always happy to help.
          </p>
          <a 
            href="https://wa.me/918137830916" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#25D366] text-white font-medium rounded-lg hover:opacity-90 transition-opacity text-sm"
          >
            <MessageCircle className="w-4 h-4" />
            Chat on WhatsApp
          </a>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-[#B8860B]/70">
            © 2026 Mothers Gold & Diamonds LLP. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default HelpPageNew;
